<div class="relative overflow-hidden rounded-2xl border border-gray-6 bg-surface-default shadow-sm">
  <!-- Map Header Bar -->
  <div
    class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-6 bg-gray-2/60 px-4 py-3 sm:px-5"
  >
    <div class="flex items-center gap-2.5">
      <div
        class="flex h-7 w-7 items-center justify-center rounded-lg bg-jade-a2 text-jade-11 border border-jade-a4"
      >
        <mat-icon class="text-base leading-none">layers</mat-icon>
      </div>
      <div>
        <span class="text-sm font-bold text-gray-12">Plan parcellaire satellite</span>
      </div>
    </div>

    <!-- Controls: Rayon + Compteur -->
    <div class="flex items-center gap-2">
      <!-- Sélecteur de rayon utilisant la couleur primary -->
      <div class="inline-flex rounded-lg border border-gray-6 bg-surface-default p-0.5 shadow-2xs">
        <button
          type="button"
          class="rounded-md px-2.5 py-1 text-xs font-semibold transition-all cursor-pointer"
          [class.bg-primary]="radius() === 25"
          [class.text-primary-contrast]="radius() === 25"
          [class.shadow-2xs]="radius() === 25"
          [class.text-gray-11]="radius() !== 25"
          [class.hover:text-gray-12]="radius() !== 25"
          (click)="setRadius(25)"
        >
          25 m
        </button>
        <button
          type="button"
          class="rounded-md px-2.5 py-1 text-xs font-semibold transition-all cursor-pointer"
          [class.bg-primary]="radius() === 50"
          [class.text-primary-contrast]="radius() === 50"
          [class.shadow-2xs]="radius() === 50"
          [class.text-gray-11]="radius() !== 50"
          [class.hover:text-gray-12]="radius() !== 50"
          (click)="setRadius(50)"
        >
          50 m
        </button>
      </div>

      <!-- Compteur de lots trouvés -->
      @if (parcels().length > 0) {
        <span
          class="inline-flex items-center gap-1 rounded-full border border-gray-6 bg-gray-2 px-2.5 py-0.5 text-xs text-gray-11"
        >
          <span class="h-1.5 w-1.5 rounded-full bg-jade-9"></span>
          <span>{{ parcels().length }} lots</span>
        </span>
      }
    </div>
  </div>

  <!-- Map Viewport -->
  <div class="relative h-72 w-full sm:h-96">
    <div #mapContainer class="h-full w-full"></div>

    <!-- Loading Spinner Overlay -->
    @if (isLoading()) {
      <div
        class="absolute inset-0 z-500 flex items-center justify-center bg-surface-default/60 backdrop-blur-2xs"
      >
        <div
          class="flex items-center gap-3 rounded-xl border border-gray-6 bg-surface-default px-4 py-2.5 shadow-md"
        >
          <mat-spinner diameter="20"></mat-spinner>
          <span class="text-xs font-medium text-gray-11">Chargement des parcelles cadastrales...</span>
        </div>
      </div>
    }

    <!-- Légende flottante en haut à gauche avec container dark glass et texte haute visibilité -->
    <div
      class="pointer-events-none absolute left-3 top-3 z-400 hidden sm:flex flex-col gap-2 rounded-xl border border-gray-a5 bg-gray-12/90 p-3 backdrop-blur-md shadow-2xl text-2xs"
    >
      <div class="flex items-center gap-2">
        <span class="h-2.5 w-2.5 rounded-full bg-blue-9 border border-gray-contrast shadow-xs"></span>
        <span class="font-bold text-gray-contrast tracking-wide [text-shadow:0_1px_3px_rgba(0,0,0,0.95)]">
          Adresse géocodée
        </span>
      </div>
    </div>

    <!-- Panneau de sélection de lot en bas à gauche : container dark glass, texte contrasté -->
    @if (activeParcel(); as selected) {
      <div
        class="absolute bottom-3 left-3 z-400 rounded-xl border border-gray-a5 bg-gray-12/90 p-3.5 backdrop-blur-md shadow-2xl max-w-[calc(100%-80px)] sm:max-w-sm"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span
                class="font-mono text-sm font-bold text-gray-contrast tracking-wide [text-shadow:0_1px_3px_rgba(0,0,0,0.95)]"
              >
                Section {{ selected.section }} · N° {{ selected.numero }}
              </span>
              @if (isSelectedParcel(selected)) {
                <span
                  class="inline-flex items-center gap-1 rounded-full border border-jade-a6 bg-jade-a3 px-2 py-0.5 text-2xs font-bold text-jade-11 shadow-xs"
                >
                  Sélectionnée
                </span>
              }
              @if (!selected.hasBdnbBuilding) {
                <span
                  class="inline-flex items-center gap-1 rounded-full border border-amber-a6 bg-amber-a3 px-2 py-0.5 text-2xs font-semibold text-amber-11 shadow-xs"
                >
                  Non bâti (BDNB)
                </span>
              }
            </div>
            <div class="mt-1 flex items-center gap-2 text-xs">
              @if (selected.surface) {
                <span class="font-bold text-gray-contrast [text-shadow:0_1px_2px_rgba(0,0,0,0.95)]"
                  >{{ selected.surface }} m²</span
                >
                <span class="text-gray-9">•</span>
              }
              <span
                class="truncate font-mono text-2xs text-gray-8 [text-shadow:0_1px_2px_rgba(0,0,0,0.95)]"
                >{{ selected.id }}</span
              >
            </div>
          </div>

          @if (interactive()) {
            @if (isSelectedParcel(selected)) {
              <button
                type="button"
                class="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-ruby-9 hover:bg-ruby-10 text-white px-3 py-1.5 text-xs font-bold shadow-md transition-colors cursor-pointer"
                (click)="selectParcel(selected)"
              >
                <mat-icon class="text-sm leading-none">remove</mat-icon>
                <span>Retirer</span>
              </button>
            } @else {
              <button
                type="button"
                class="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-jade-9 hover:bg-jade-10 text-jade-contrast px-3 py-1.5 text-xs font-bold shadow-md transition-colors cursor-pointer"
                (click)="selectParcel(selected)"
              >
                <mat-icon class="text-sm leading-none">add</mat-icon>
                <span>Ajouter</span>
              </button>
            }
          }
        </div>
      </div>
    }
  </div>
</div>

<style>
  :host ::ng-deep .leaflet-tooltip.cadastral-tooltip {
    background: var(--color-gray-12) !important;
    border: 1px solid var(--color-gray-a6) !important;
    border-radius: 6px !important;
    color: var(--color-gray-contrast) !important;
    text-shadow:
      0 1px 3px rgba(0, 0, 0, 0.9),
      0 0 4px rgba(0, 0, 0, 0.8) !important;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    padding: 3px 8px !important;
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.5),
      0 0 0 1px rgba(0, 0, 0, 0.3) !important;
    backdrop-filter: blur(6px) !important;
    white-space: nowrap !important;
  }

  :host ::ng-deep .leaflet-tooltip.cadastral-tooltip.no-building {
    opacity: 0.65 !important;
    background: var(--color-gray-11) !important;
    border: 1px dashed var(--color-gray-8) !important;
    font-style: italic !important;
  }

  :host ::ng-deep .leaflet-tooltip.cadastral-tooltip.current {
    opacity: 1 !important;
    background: var(--color-jade-12) !important;
    border: 1.5px solid var(--color-jade-9) !important;
    color: var(--color-jade-contrast) !important;
    text-shadow:
      0 1px 3px rgba(0, 0, 0, 0.9),
      0 0 8px var(--color-jade-9) !important;
    box-shadow:
      0 0 14px var(--color-jade-9),
      0 4px 10px rgba(0, 0, 0, 0.5) !important;
  }

  :host ::ng-deep .leaflet-tooltip.cadastral-tooltip::before {
    display: none !important;
  }
</style>
